import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { LoadingState } from '@nocowanie/core/enums';

import { SEARCH_CONSTS } from '@app/consts';
import { SliceNameEnum } from '@app/enums';
import { ListingState } from '@app/models';
import { fetchCampaignListingMeta, fetchListingItems } from '@app/state/api-thunks/listing.thunk';

export const initialState: ListingState = {
    meta: {
        errors: null,
        loading: LoadingState.IDLE,
        data: {
            pageTitle: '',
            header: '',
            pageCount: 1,
            itemCount: 0,
            cmsId: undefined,
            checkIn: undefined,
            checkOut: undefined,
            flexDates: undefined,
        },
    },
    items: {
        errors: null,
        loading: LoadingState.IDLE,
        data: null,
    },
};

export const listingStateSlice = createSlice({
    name: SliceNameEnum.Listing,
    initialState,
    reducers: {
        setMeta: (state, action: PayloadAction<ListingState['meta']>) => {
            state.meta = { ...action.payload };
        },
        setItems: (state, action: PayloadAction<ListingState['items']>) => {
            state.items = { ...action.payload };
        },
    },
    extraReducers: builder => {
        builder.addCase(fetchCampaignListingMeta.pending, state => {
            state.meta.loading = LoadingState.PENDING;
        });

        builder.addCase(fetchCampaignListingMeta.rejected, (state, { payload }) => {
            state.meta.errors = [payload as string];
            state.meta.loading = LoadingState.ERROR;
        });

        builder.addCase(fetchCampaignListingMeta.fulfilled, (state, { payload }) => {
            if (payload) {
                state.meta.data = {
                    pageTitle: payload.campaignTitle || '',
                    header: payload.listingHeader || '',
                    cmsId: payload.documentId,
                    pageCount: state.meta.data.pageCount,
                    itemCount: state.meta.data.itemCount,
                    checkIn: payload?.checkIn ?? null,
                    checkOut: payload?.checkOut ?? null,
                    flexDates: payload?.flexDates
                        ? Number(payload.flexDates.replace('d-', ''))
                        : null,
                };
            }
            state.meta.errors = null;
            state.meta.loading = LoadingState.SUCCESS;
        });

        builder.addCase(fetchListingItems.pending, state => {
            state.items.loading = LoadingState.PENDING;
        });

        builder.addCase(fetchListingItems.rejected, (state, { payload }) => {
            state.items.errors = [payload as string];
            state.items.loading = LoadingState.ERROR;
        });

        builder.addCase(fetchListingItems.fulfilled, (state, { payload }) => {
            state.items.data = payload
                ? {
                      ...payload,
                  }
                : null;
            state.items.errors = null;
            state.items.loading = LoadingState.SUCCESS;

            if (payload?.totalItems !== undefined) {
                state.meta.data.itemCount = payload.totalItems;
                state.meta.data.pageCount =
                    payload.totalItems > 0
                        ? Math.ceil(payload.totalItems / SEARCH_CONSTS.PAGE_ITEM_COUNT)
                        : 0;
            }
        });
    },
});

export const { setMeta, setItems } = listingStateSlice.actions;

const selectSelfState = (state: { [SliceNameEnum.Listing]: ListingState }): ListingState => {
    return state[listingStateSlice.name] ?? initialState;
};

export const selectors = {
    selectListingMeta: createSelector(selectSelfState, state => state.meta),
    selectListingCmsId: createSelector(selectSelfState, state => state.meta.data.cmsId),
    selectListingItems: createSelector(selectSelfState, state => state.items.data?.tiles),
    selectListingRealTimeItemCount: createSelector(
        selectSelfState,
        state => state.items.data?.realTimeItems ?? 0,
    ),
    selectIsListingItemsLoading: createSelector(
        selectSelfState,
        state => state.items.loading === LoadingState.PENDING,
    ),
    selectIsListingItemsError: createSelector(
        selectSelfState,
        state => state.items.loading === LoadingState.ERROR,
    ),
};

export default listingStateSlice.reducer;
